import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ScheduleIcon from "@mui/icons-material/Schedule"; // For appointment schedule
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"; // For completed jobs
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonIcon from "@mui/icons-material/Person"; // For IPEN sessions
import { convertIsoToLocalFormat } from "../../functions/helperFunctions";

import NotaryCard from "./dashboardComponents/NotaryCard";
import NotaryPerformanceChart from "./charts/NotaryPerformanceChart";
import NotaryJobNotice from "./dashboardComponents/NotaryJobNotice";
import MarkRonAsExpired from "./modals/MarkRonAsExpired";
import DisplayLogs from "./modals/DisplayLogs";

const NotaryDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [loadingPage, setLoadingPage] = useState(false);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state.notarySession.user);
  const notaryJobs = useSelector((state) => state.notarySession.notaryJobs);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [years, setYears] = useState([year]);
  const [closestJobDate, setClosestJobDate] = useState("N/A");
  const [secondClosestJobDate, setSecondClosestJobDate] = useState("N/A");
  const [fiveMinBeforeSession, setFiveMinBeforeSession] = useState(false);

  const [infoModal, setInfoModal] = useState({
    voidLoading: false,
    logsOpen: false,
    rescheduleLoading: false,
    rescheduleOpen: false,
    completedLoading: false,
    alertText: "",
    alertOpen: false,
    alertSeverity: "success",
  });

  const [elapsed, setElapsed] = useState({
    hours: 0,
    minutes: 0,
  });

  useEffect(() => {
    if (currentUser?.closest_job && currentUser?.closest_job?.booked_date) {
      const closestIso = currentUser.closest_job.booked_date;
      const userZone = currentUser?.timezone || "America/New_York";

      // Convert the closestIso to the user's local time format
      const dateString = convertIsoToLocalFormat(
        closestIso,
        userZone,
        "cccc, LLLL dd, yyyy hh:mm a",
      );
      setClosestJobDate(dateString);

      // Convert closestIso to a Date object in UTC
      const closestDateUTC = DateTime.fromISO(closestIso, { zone: "utc" });

      const checkTimeDifference = () => {
        // Get the current time in UTC
        const nowUTC = DateTime.utc();

        // Convert both dates to UTC for comparison
        const timeDifference = closestDateUTC.toMillis() - nowUTC.toMillis();

        // Check if the difference is less than 10 minutes (300,000 milliseconds)
        if (timeDifference <= 300000 && fiveMinBeforeSession === false) {
          setFiveMinBeforeSession(true);
        } else {
          setFiveMinBeforeSession(false);
        }
      };

      // Perform the initial check
      checkTimeDifference();

      const intervalId = setInterval(checkTimeDifference, 30000);

      // Clear the interval when the component unmounts or dependencies change
      return () => clearInterval(intervalId);
    } else {
      setClosestJobDate("N/A");
      setFiveMinBeforeSession(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (
      currentUser?.second_closest_date &&
      currentUser.second_closest_date !== "N/A"
    ) {
      const secondClosestIso = currentUser.second_closest_date;
      const userZone = currentUser?.timezone || "America/New_York";

      // Convert the secondClosestIso to the user's local time format
      const dateString = convertIsoToLocalFormat(
        secondClosestIso,
        userZone,
        "cccc, LLLL dd, yyyy hh:mm a",
      );

      setSecondClosestJobDate(dateString);
    } else {
      setSecondClosestJobDate("N/A");
    }
  }, [currentUser?.second_closest_date, currentUser?.timezone]);

  useEffect(() => {
    if (!currentUser || !notaryJobs) {
      return;
    }
    if (!currentUser?.role === "notary") {
      navigate("/login");
    }

    if (currentUser?.availabilities.length === 0) {
      navigate("/notary/finalize");
      return;
    }

    setLoadingPage(false);
  }, [currentUser, notaryJobs, navigate]);

  const handleExpireRON = async () => {
    setInfoModal((prev) => ({ ...prev, rescheduleLoading: true }));
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/expire-ron-session`,
        {
          task_id: currentUser?.closest_job?.id,
          bn_id: currentUser?.closest_job?.notarization?.notarization?.bn_id,
          signer: currentUser?.closest_job?.signer,
          currentSession: currentSession,
          instant:
            currentUser?.closest_job?.notarization?.notarization?.instant_token,
        },
        {
          withCredentials: true,
        },
      );
      if (resp.data.success === true) {
        setInfoModal((prev) => ({
          ...prev,
          alertText: resp.data.message,
          alertOpen: true,
          alertSeverity: "success",
        }));
      }
    } catch (e) {
      setInfoModal((prev) => ({
        ...prev,
        alertText: e.response.data.message,
        alertOpen: true,
        alertSeverity: "error",
      }));
    } finally {
      setInfoModal((prev) => ({
        ...prev,
        rescheduleLoading: false,
        rescheduleOpen: false,
      }));
    }
  };
  //! /////////////////////////////////////////////////////////////////
  const handleSetCompleted = async () => {
    setInfoModal((prev) => ({ ...prev, completedLoading: true }));
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/set-completed-notarization`,
        {
          task_id: currentUser?.closest_job?.id,
          bn_id: currentUser?.closest_job?.notarization?.notarization?.bn_id,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        },
      );
      if (resp.data.success === true) {
      }
    } catch (e) {
      setInfoModal((prev) => ({
        ...prev,
        alertText: e.response.data.message,
        alertOpen: true,
        alertSeverity: "error",
      }));
    } finally {
      setInfoModal((prev) => ({ ...prev, completedLoading: false }));
    }
  };

  const snackHandleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setInfoModal((prev) => ({ ...prev, alertOpen: false, alertText: "" }));
  };

  useEffect(() => {
    if (currentUser?.activity) {
      const yearsSet = new Set();

      currentUser?.activity.forEach((item) => {
        yearsSet.add(item.year);
      });

      setYears([...yearsSet].sort((a, b) => a - b));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChartValues = () => {
    let completedJobs = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    let acceptedJobs = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    let cancelledJobs = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-indexed (0 = January, 11 = December)

    // First, populate from activity data
    currentUser?.activity.map((item) => {
      if (item?.year === year) {
        completedJobs[item.month] = item.completed;
        acceptedJobs[item.month] = item.accepted;
        cancelledJobs[item.month] = item.cancelled;
      }
      return null;
    });

    // For current year and current month, ensure we have up-to-date data
    // If activity data for current month is missing or zero, use dynamic counts as fallback
    if (parseInt(year) === currentYear) {
      const currentMonthActivity = currentUser?.activity?.find(
        (item) => item?.year === currentYear && item?.month === currentMonth,
      );

      // If no activity data for current month, or if it seems stale, use dynamic counts
      if (!currentMonthActivity || currentMonthActivity.completed === 0) {
        const completedCount =
          currentUser?.completed_count ?? currentUser?.completed_jobs ?? 0;
        const pendingCount =
          currentUser?.pending_count ?? currentUser?.pending_jobs ?? 0;
        const expiredCount =
          currentUser?.expired_count ?? currentUser?.cancelled_jobs ?? 0;

        // Calculate current month's values from dynamic counts
        // Sum all previous months' completed jobs
        const previousMonthsCompleted = completedJobs
          .slice(0, currentMonth)
          .reduce((sum, val) => sum + val, 0);
        // Current month's completed = total completed - previous months
        const currentMonthCompleted = Math.max(
          0,
          completedCount - previousMonthsCompleted,
        );

        // Similar calculation for accepted and expired
        const previousMonthsAccepted = acceptedJobs
          .slice(0, currentMonth)
          .reduce((sum, val) => sum + val, 0);
        const totalAccepted = pendingCount + completedCount;
        const currentMonthAccepted = Math.max(
          0,
          totalAccepted - previousMonthsAccepted,
        );

        const previousMonthsExpired = cancelledJobs
          .slice(0, currentMonth)
          .reduce((sum, val) => sum + val, 0);
        const currentMonthExpired = Math.max(
          0,
          expiredCount - previousMonthsExpired,
        );

        // Update current month with calculated values
        completedJobs[currentMonth] = currentMonthCompleted;
        acceptedJobs[currentMonth] = currentMonthAccepted;
        cancelledJobs[currentMonth] = currentMonthExpired;
      }
    }

    return [
      {
        name: "Completed",
        type: "column",
        data: completedJobs,
      },
      {
        name: "Total Accepted",
        type: "area",
        data: acceptedJobs,
      },
      {
        name: "Expired",
        type: "line",
        data: cancelledJobs,
      },
    ];
  };

  return (
    <>
      {loadingPage ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress
              color="warning"
              size={isMobile ? "4rem" : isTablet ? "6rem" : "10rem"}
            />
          </Box>
        </>
      ) : (
        <>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: {
                xs: 0.5, // Mobile: smaller vertical padding
                sm: 1, // Tablet: medium padding
                md: 1, // Desktop: original padding
              },
              my: {
                xs: 1, // Mobile: smaller vertical margin
                sm: 2, // Tablet and up: original margin
              },
              backgroundColor: "#EEF2F6",
              minHeight: "100vh", // Ensure full height on mobile
            }}
          >
            <Container
              maxWidth="xl"
              sx={{
                px: {
                  xs: 1, // Mobile: smaller horizontal padding
                  sm: 2, // Tablet: medium padding
                  md: 3, // Desktop: larger padding
                },
              }}
            >
              <Grid
                container
                spacing={{
                  xs: 1, // Mobile: smaller spacing
                  sm: 2, // Tablet and up: original spacing
                }}
                justifyContent="center"
              >
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <NotaryCard
                    title="Completed"
                    value={
                      currentUser?.completed_count ??
                      currentUser?.completed_jobs ??
                      0
                    }
                    icon={CheckCircleOutlineIcon}
                    color="success.main"
                    tooltip="Jobs successfully completed"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <NotaryCard
                    title="Pending"
                    value={
                      currentUser?.pending_count ??
                      currentUser?.pending_jobs ??
                      0
                    }
                    icon={TaskAltIcon}
                    color="warning.main"
                    tooltip="Your Pending Jobs"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <NotaryCard
                    title="Expired"
                    value={
                      currentUser?.expired_count ??
                      currentUser?.cancelled_jobs ??
                      0
                    }
                    icon={CancelIcon}
                    color="error.main"
                    tooltip="Time for your next notarization"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <NotaryCard
                    title="Upcoming Notarization"
                    value={secondClosestJobDate}
                    icon={ScheduleIcon}
                    color="info.main"
                    tooltip="Time for your upcoming notarization"
                    isUpcoming={true}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <NotaryCard
                    title="Active IPEN Sessions"
                    value={currentUser?.active_ipen_sessions?.length || 0}
                    icon={PersonIcon}
                    color="secondary.main"
                    tooltip="Active In-Person Electronic Notarization sessions"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <NotaryJobNotice
                    currentUser={currentUser}
                    handleSetCompleted={handleSetCompleted}
                    infoModal={infoModal}
                    setInfoModal={setInfoModal}
                    closestJobDate={closestJobDate}
                    fiveMinBeforeSession={fiveMinBeforeSession}
                    elapsed={elapsed}
                    setElapsed={setElapsed}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <NotaryPerformanceChart
                    year={year}
                    setYear={setYear}
                    years={years}
                    handleChartValues={handleChartValues}
                    chartSeries={handleChartValues()}
                    sx={{
                      height: "100%",
                      minHeight: {
                        xs: "300px", // Mobile: ensure minimum height for chart
                        sm: "400px", // Tablet: medium height
                        md: "500px", // Desktop: larger height
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Snackbar
                open={infoModal.alertOpen}
                autoHideDuration={10000}
                onClose={snackHandleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: isMobile ? "center" : "center",
                }}
                sx={{
                  mt: {
                    xs: "60px", // Mobile: account for smaller screens
                    sm: "50px", // Tablet and up: original margin
                  },
                  ml: lgUp ? "150px" : undefined,
                  mx: {
                    xs: 2, // Mobile: add horizontal margin to prevent edge touching
                    sm: 0, // Tablet and up: no horizontal margin
                  },
                }}
              >
                <Alert
                  onClose={snackHandleClose}
                  severity={infoModal.alertSeverity}
                  sx={{
                    width: "100%",
                    backgroundColor:
                      infoModal.alertSeverity === "success"
                        ? "#388e3c" // Green shade for success
                        : "#f04438", // Red shade for error
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: {
                      xs: "0.875rem", // Mobile: smaller text
                      sm: "1rem", // Tablet and up: standard text
                    },
                    "& .MuiAlert-message": {
                      width: "100%",
                      wordBreak: "break-word", // Prevent text overflow on mobile
                    },
                  }}
                  iconMapping={{
                    success: <CheckCircleIcon sx={{ color: "#fff" }} />, // Changed to success icon
                    error: <CancelIcon sx={{ color: "#fff" }} />, // Changed to error icon
                  }}
                >
                  {infoModal.alertText}
                </Alert>
              </Snackbar>
            </Container>
          </Box>
          <MarkRonAsExpired
            infoModal={infoModal}
            setInfoModal={setInfoModal}
            elapsed={elapsed}
            handleExpireRON={handleExpireRON}
          />
          <DisplayLogs infoModal={infoModal} setInfoModal={setInfoModal} />
        </>
      )}
    </>
  );
};

export default NotaryDashboard;
